// Afboek-historie helpers: geeft per grondstof/hulpstof elke afboeking met datum, bron en kg.
const N = (v) => { const n = Number(v); return isNaN(n) ? 0 : n; };
const byDateDesc = (a, b) => (String(a.datum || "") < String(b.datum || "") ? 1 : String(a.datum || "") > String(b.datum || "") ? -1 : 0);

const codeFromBatch = (batch) => String(
  batch?.eigenTracecode || batch?.tracecode || batch?.batchnr || "",
).trim();

const lotcodesByRun = (runs) => {
  const seqByDate = {};
  const out = new Map();
  [...(runs || [])]
    .filter(Boolean)
    .sort((a, b) => String(a.datum || "").localeCompare(String(b.datum || "")))
    .forEach((run) => {
      const dateCode = String(run.datum || "").replace(/-/g, "");
      seqByDate[dateCode] = (seqByDate[dateCode] || 0) + 1;
      out.set(
        String(run.id),
        String(run.lotcode || `HF-${dateCode}-${String(seqByDate[dateCode]).padStart(2, "0")}`),
      );
    });
  return out;
};

export function grondstofAfboekingen(naam, entries, halffabRuns, afkeur, expeditie = []) {
  const out = [];
  const expById = new Map((expeditie || []).map((batch) => [String(batch.id), batch]));
  (afkeur || []).forEach((a) => {
    if (a && !a.teruggedraaid && a.categorie === "Grondstof" && a.naam === naam && N(a.kg) > 0.0001) {
      out.push({
        datum: a.datum || "",
        bron: `Afkeur: ${a.reden || ""}`,
        detail: a.tracecode ? `tracecode ${a.tracecode}` : (a.opmerking || ""),
        kg: N(a.kg),
        type: "Afkeur",
        door: a.door || "",
        tracecode: String(a.tracecode || "").trim(),
      });
    }
  });
  (entries || []).forEach((e) => {
    if (e && e.grondstof === naam) {
      const kg = Math.max(N(e.grondstofKg) - N(e.restantKg), 0);
      if (kg > 0.0001) {
        out.push({
          datum: e.datum || "",
          bron: e.product || "Productie",
          detail: e.lijn || "",
          kg,
          type: "Productie",
          door: e.door || "",
          tracecode: codeFromBatch(expById.get(String(e.expeditieId || ""))),
        });
      }
    }
  });
  (halffabRuns || []).forEach((r) => {
    (r.verbruik || []).forEach((v) => {
      if (v && v.categorie === "Grondstof" && v.naam === naam && N(v.kg) > 0.0001)
        out.push({
          datum: r.datum || "",
          bron: `Halffabricaat: ${r.recept || ""}`,
          detail: v.partijnummer ? `partij ${v.partijnummer}` : "",
          kg: N(v.kg),
          type: "Halffabricaat",
          door: r.door || "",
          tracecode: String(
            v.eigenTracecode || v.batchnr || codeFromBatch(expById.get(String(v.expeditieId || ""))),
          ).trim(),
        });
    });
  });
  return out.sort(byDateDesc);
}

export function hulpstofAfboekingen(naam, entries, halffabRuns, afkeur, hulpstofInkoop = []) {
  const out = [];
  const hulpByName = new Map();
  (hulpstofInkoop || []).forEach((batch) => {
    if (!batch?.hulpstof) return;
    const list = hulpByName.get(batch.hulpstof) || [];
    list.push(batch);
    hulpByName.set(batch.hulpstof, list);
  });
  const codeForHulp = (hulpstof) => {
    const latest = (hulpByName.get(hulpstof) || [])
      .slice()
      .sort((a, b) => String(b.datum || "").localeCompare(String(a.datum || "")))[0];
    return codeFromBatch(latest);
  };
  (afkeur || []).forEach((a) => {
    if (a && !a.teruggedraaid && a.categorie === "Hulpstof" && a.naam === naam && N(a.kg) > 0.0001) {
      out.push({
        datum: a.datum || "",
        bron: `Afkeur: ${a.reden || ""}`,
        detail: a.tracecode ? `tracecode ${a.tracecode}` : (a.opmerking || ""),
        kg: N(a.kg),
        type: "Afkeur",
        door: a.door || "",
        tracecode: String(a.tracecode || "").trim(),
      });
    }
  });
  const fields = [["predust", "predustKg"], ["battermix", "battermixKg"], ["breader", "breaderKg"], ["hamkaas", "hamkaasKg"]];
  (entries || []).forEach((e) => {
    if (!e) return;
    fields.forEach(([f, kf]) => {
      if (e[f] === naam && N(e[kf]) > 0.0001) {
        out.push({
          datum: e.datum || "",
          bron: e.product || "Productie",
          detail: e.lijn || "",
          kg: N(e[kf]),
          type: "Productie",
          door: e.door || "",
          tracecode: String(e[`${f}StockLots`]?.map((lot) => lot.tracecode).filter(Boolean).join(", ") || e[`${f}Trace`] || codeForHulp(naam)).trim(),
        });
      }
    });
  });
  (halffabRuns || []).forEach((r) => {
    (r.verbruik || []).forEach((v) => {
      if (v && v.categorie === "Hulpstof" && v.naam === naam && N(v.kg) > 0.0001)
        out.push({
          datum: r.datum || "",
          bron: `Halffabricaat: ${r.recept || ""}`,
          detail: "",
          kg: N(v.kg),
          type: "Halffabricaat",
          door: r.door || "",
          tracecode: String(v.eigenTracecode || v.batchnr || v.partijnummer || codeForHulp(naam)).trim(),
        });
    });
  });
  return out.sort(byDateDesc);
}

// Afboek-historie voor een halffabricaat: waar/wanneer het is verbruikt
// (in de productie-invoer als grondstof, of als ingrediënt in een ander halffabricaat).
export function halffabAfboekingen(naam, entries, halffabRuns) {
  const out = [];
  const lotByRun = lotcodesByRun(halffabRuns);
  (entries || []).forEach((e) => {
    if (e && e.grondstof === naam) {
      const kg = Math.max(N(e.grondstofKg) - N(e.restantKg), 0);
      if (kg > 0.0001) {
        const lots = Array.isArray(e.halffabLots) && e.halffabLots.length
          ? e.halffabLots
          : [{ runId: e.halffabRunId, kg }];
        lots.forEach((lot) => {
          const usedKg = N(lot.kg) || kg;
          out.push({
            datum: e.datum || "",
            bron: e.product || "Productie",
            detail: e.lijn || "",
            kg: usedKg,
            type: "Productie",
            tracecode: lotByRun.get(String(lot.runId || "")) || "",
          });
        });
      }
    }
  });
  (halffabRuns || []).forEach((r) => {
    (r.verbruik || []).forEach((v) => {
      if (v && v.categorie === "Halffabricaat" && v.naam === naam && N(v.kg) > 0.0001)
        out.push({
          datum: r.datum || "",
          bron: `Halffabricaat: ${r.recept || ""}`,
          detail: "",
          kg: N(v.kg),
          type: "Halffabricaat",
          tracecode: String(v.lotcode || lotByRun.get(String(v.runId || "")) || "").trim(),
        });
    });
  });
  return out.sort(byDateDesc);
}
